/**
 * US-59 #8: контекст бейджей навигации.
 *
 * Раньше бейджи `useNotificationBadges()` опрашивались с интервалом 30s.
 * После каждого действия (submit / approve / return / login / delete)
 * пользователь видел задержку до 30s до того, как кружок поменялся —
 * раздражает.
 *
 * Решение: оборачиваем `useNotificationBadges` в один общий Context,
 * прокидываем `refresh()` в дерево компонентов через `useBadges()`.
 * Каждое мутирующее действие вызывает `void refresh()` сразу после
 * успешного await — счётчик обновляется в течение ~ network roundtrip.
 *
 * Polling 30s остаётся как страховка (изменения, сделанные другими
 * пользователями, тоже подтянутся).
 */

import { createContext, useContext, type ReactNode } from "react";

import type { NotificationBadges } from "../api/client";
import { useNotificationBadges } from "./useNotificationBadges";

interface BadgesContextValue {
  badges: NotificationBadges | null;
  /** Принудительно обновить бейджи (после mutating action). */
  refresh: () => Promise<void>;
}

const BadgesContext = createContext<BadgesContextValue | null>(null);

export function BadgesProvider({ children }: { children: ReactNode }) {
  const { badges, refresh } = useNotificationBadges();
  return (
    <BadgesContext.Provider value={{ badges, refresh }}>{children}</BadgesContext.Provider>
  );
}

/**
 * No-op fallback на случай рендера компонентов вне `<BadgesProvider>` —
 * прежде всего это юнит-тесты (vitest рендерит компоненты в `<MantineProvider>`
 * без оборачивания в App-дерево). В рантайме SPA дерево всегда обёрнуто
 * в provider (см. `main.tsx`).
 */
const NOOP_BADGES_CONTEXT: BadgesContextValue = {
  badges: null,
  refresh: async () => undefined,
};

/**
 * Возвращает бейджи + функцию принудительного refresh.
 * Использовать в компонентах внутри `<BadgesProvider>`. Если провайдера
 * нет (юнит-тесты, изолированный рендер компонента) — возвращаем no-op,
 * чтобы тесты не падали из-за инфраструктуры бейджей.
 */
export function useBadges(): BadgesContextValue {
  return useContext(BadgesContext) ?? NOOP_BADGES_CONTEXT;
}
